/**
 * Shared visual building blocks for Knowledge Base sources: status pill
 * metadata, type icons, product-version labelling, skill palette, and skill-id
 * resolution.
 */
import Icon from './Icon'

export const STATUS_META = {
  indexed:        { className: 'indexed',       label: 'Indexed' },
  pending:        { className: 'pending',       label: 'Pending' },
  processing:     { className: 'processing',    label: 'Processing' },
  pending_delete: { className: 'pendingDelete', label: 'Deleting' },
  error:          { className: 'error',         label: 'Error' },
}

export function statusMetaFor(status) {
  return STATUS_META[status] ?? STATUS_META.indexed
}

/**
 * Statuses ranked by what they want from the reader, most demanding first.
 *
 * Error tops the list because it's the only state that will never resolve on
 * its own. The three in-flight states follow — all of them mean "wait", so they
 * only order among themselves: processing is actively underway, pending is
 * merely queued, and a delete comes last of the three because the reader
 * started it seconds ago and already knows. Indexed is the nothing-to-see
 * state, so it surfaces only when nothing else is present.
 */
const STATUS_PRIORITY = ['error', 'processing', 'pending', 'pending_delete', 'indexed']

/**
 * Collapses a document's content-sets into per-status counts, ranked.
 * @param {Array<{status: string}>} contentSets
 * @returns {Array<{status: string, count: number}>}
 */
function rollupStatuses(contentSets = []) {
  const counts = new Map()
  contentSets.forEach(cs => counts.set(cs.status, (counts.get(cs.status) ?? 0) + 1))
  return STATUS_PRIORITY
    .filter(status => counts.has(status))
    .map(status => ({ status, count: counts.get(status) }))
}

/**
 * The one status a collapsed list row shows, plus the numbers to qualify it.
 *
 * A document can hold a content-set per product version, so printing a pill
 * apiece stops scaling around three or four versions. The row instead reports
 * only its most demanding status and how much of the document is in it —
 * "Error 1/9" says both that something needs attention and that the other eight
 * versions are fine. `breakdown` carries the rest for the tooltip, and
 * expanding the row shows every version outright.
 *
 * @param {Array<{status: string}>} contentSets
 * @returns {{status: string, count: number, total: number, breakdown: string}|null}
 */
export function statusSummary(contentSets = []) {
  const rollup = rollupStatuses(contentSets)
  if (!rollup.length) return null
  const [{ status, count }] = rollup
  return {
    status,
    count,
    total: contentSets.length,
    breakdown: rollup.map(r => `${r.count} ${statusMetaFor(r.status).label.toLowerCase()}`).join(', '),
  }
}

/**
 * How urgently a document's content-sets want attention, lowest first — the
 * ordering the Status column sorts on.
 *
 * Rank comes first, then how much of the document sits at that rank, so five
 * failed versions out of nine outrank a single failure. The share is a
 * fraction, so it only ever breaks ties inside a rank and never lifts a
 * document past one with a more demanding status. A document with no
 * content-sets at all sorts last.
 *
 * @param {Array<{status: string}>} contentSets
 */
export function statusSeverity(contentSets = []) {
  const summary = statusSummary(contentSets)
  if (!summary) return STATUS_PRIORITY.length
  return STATUS_PRIORITY.indexOf(summary.status) + (1 - summary.count / summary.total)
}

/**
 * What the delete action should offer for a set of content-sets.
 *
 * Only a mid-ingest content-set blocks a delete — that is the one state the
 * backend answers with a 409. Something already marked Deleting does NOT block:
 * repeating the delete re-enqueues the teardown, which is how a teardown that
 * never finished (a message that DLQ'd, a worker that died) is recovered. Since
 * the page keeps polling while anything is in flight, a stalled delete would
 * otherwise leave the row spinning with no way out.
 *
 * It only counts as a retry when EVERY content-set in the set is already
 * Deleting — the request being repeated. A document with one stalled version
 * among several live ones is a fresh delete of the whole document that happens
 * to re-cover the stalled one, and calling that a retry would undersell it.
 *
 * @param {Array<{status: string}>} contentSets
 * @returns {{blocked: boolean, retry: boolean}}
 */
export function deleteAffordance(contentSets = []) {
  const blocked = contentSets.some(cs => cs.status === 'processing')
  return {
    blocked,
    retry: !blocked && contentSets.length > 0
      && contentSets.every(cs => cs.status === 'pending_delete'),
  }
}

// A content-set with no product version carries the backend's sentinel rather
// than a NULL. It means "applies to every version of the product", and
// retrieval always considers it eligible — so it's shown as the sentinel
// itself, not as an absence.
export const SENTINEL_PRODUCT_VERSION = '-'
export const VERSION_AGNOSTIC_TITLE = 'Applies to all versions'

export function productVersionLabel(productVersion) {
  return productVersion || SENTINEL_PRODUCT_VERSION
}

// Tooltip for a version label — names what the bare sentinel means, and leaves
// a real version to speak for itself.
export function productVersionTitle(productVersion) {
  return productVersionLabel(productVersion) === SENTINEL_PRODUCT_VERSION
    ? VERSION_AGNOSTIC_TITLE
    : productVersion
}

// Accent color by source type. Products carry no category of their own (unlike
// the skills they replaced), so the icon keys off document-vs-web instead —
// matching the colors the page's Documents / Web Sources metric cards use.
const SOURCE_TYPE_PALETTE = {
  document: {
    text: 'var(--accent-text-blue)',
    border: 'color-mix(in srgb, var(--color-semantic-blue) var(--mix-border), transparent)',
    bg: 'color-mix(in srgb, var(--color-semantic-blue) var(--mix-bg-soft), transparent)',
  },
  web: {
    text: 'var(--accent-text-green)',
    border: 'color-mix(in srgb, var(--color-semantic-green) var(--mix-border-soft), transparent)',
    bg: 'color-mix(in srgb, var(--color-semantic-green) var(--mix-bg-soft), transparent)',
  },
}
export function sourceTypePalette(sourceType) {
  return SOURCE_TYPE_PALETTE[sourceType] ?? SOURCE_TYPE_PALETTE.document
}

// Accent color by skill type — matches the category colors SkillPill uses
// on the Skills tab. Case-insensitive; unknown types fall back to gray.
const TYPE_PALETTE = {
  core: {
    text: 'var(--accent-text-blue)',
    border: 'color-mix(in srgb, var(--color-semantic-blue) var(--mix-border), transparent)',
    bg: 'color-mix(in srgb, var(--color-semantic-blue) var(--mix-bg-soft), transparent)',
  }, // blue
  technical: {
    text: 'var(--accent-text-green)',
    border: 'color-mix(in srgb, var(--color-semantic-green) var(--mix-border-soft), transparent)',
    bg: 'color-mix(in srgb, var(--color-semantic-green) var(--mix-bg-soft), transparent)',
  }, // green
  process: {
    text: 'var(--accent-text-yellow)',
    border: 'color-mix(in srgb, var(--color-semantic-yellow) var(--mix-border), transparent)',
    bg: 'color-mix(in srgb, var(--color-semantic-yellow) var(--mix-bg-soft), transparent)',
  }, // amber
  general: {
    text: 'var(--color-gray-400)',
    border: 'color-mix(in srgb, var(--color-gray-400) var(--mix-border-soft), transparent)',
    bg: 'color-mix(in srgb, var(--color-gray-400) var(--mix-bg-soft), transparent)',
  }, // gray
}
export function skillPalette(type) {
  return TYPE_PALETTE[(type || 'general').toLowerCase()] ?? TYPE_PALETTE.general
}

// Icon id by skill type — parallel to skillPalette above, same case-insensitive
// fallback to 'general' for unknown/uncategorized types.
const TYPE_ICON = {
  core: 'skill-core',
  technical: 'skill-technical',
  process: 'shuffle', // placeholder reuse of the existing glyph pending a design pass
  general: 'skill-general',
}
export function skillTypeIcon(type) {
  return TYPE_ICON[(type || 'general').toLowerCase()] ?? TYPE_ICON.general
}

// Character-count cap for a source's display name/title.
export const MAX_NAME_LENGTH = 120

// File extensions the knowledge-base document upload accepts — mirrors the
// backend's DOCUMENT_CONTENT_TYPES allowlist (gaussiq_intelligence.core.knowledge_base.config),
// which is what actually decides the presigned upload's Content-Type. This
// list only drives the picker's `accept` attribute and the fast client-side
// check; the backend is the real gate.
export const ACCEPTED_DOCUMENT_EXTENSIONS = [
  '.pdf', '.docx', '.xlsx', '.pptx', '.odt', '.ods', '.odp',
  '.html', '.xhtml', '.md', '.adoc', '.asciidoc', '.csv',
]

// Character-count cap for a source's subtitle (page count, or a web URL).
export function truncate(text, maxLength = 100) {
  if (!text || text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}...`
}

// Resolves a skill id against the tenant's skill list; falls back to a
// placeholder if the fetch failed or the id is stale.
export function resolveSkill(skillId, availableSkills) {
  return availableSkills.find(s => s.id === skillId) ?? { id: skillId, name: `Skill #${skillId}`, type: 'general' }
}

export function DocumentGlyph() {
  return <Icon name="document" />
}

export function WebGlyph() {
  return <Icon name="globe" />
}
